import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';

const ContactAndPartnersPage = () => {
  return (
    <>
      <Helmet>
        <title>Contact - Creative Twinning | Kreativna Saradnja</title>
        <meta 
          name="description" 
          content="Get in touch with Creative Twinning project coordinators and explore partnership opportunities for heritage tourism development." 
        />
        <meta 
          name="keywords" 
          content="contact, Creative Twinning, Serbia Montenegro cooperation, heritage tourism, project coordination, partnership" 
        />
        <meta property="og:title" content="Contact - Creative Twinning" />
        <meta 
          property="og:description" 
          content="Connect with our project team and discover partnership opportunities for cross-border heritage tourism." 
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/contact-and-partners" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-primary to-secondary py-16 lg:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="font-heading font-bold text-3xl lg:text-4xl xl:text-5xl text-primary-foreground mb-6">
                  Contact
                </h1>
                <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed">
                  Connect with our project team and discover partnership opportunities for cross-border heritage tourism development.
                </p>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-16 lg:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
                {/* Contact Information - Left Column */}
                <div className="lg:col-span-1">
                  <ContactInfo />
                </div>

                {/* Contact Form - Right Column */}
                <div className="lg:col-span-2">
                  <ContactForm />
                </div>
              </div>
            </div>
          </section>

          {/* Footer CTA */}
          <section className="py-16 bg-gradient-to-r from-accent to-accent/80">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="font-heading font-semibold text-2xl lg:text-3xl text-accent-foreground mb-4">
                Ready to Explore Heritage Trails?
              </h2>
              <p className="text-lg text-accent-foreground/90 mb-8">
                Discover authentic cultural experiences and natural wonders across the Balkans through our carefully curated thematic trails.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/thematic-trails-overview"
                  className="inline-flex items-center justify-center px-6 py-3 bg-accent-foreground text-accent font-medium rounded-md hover:bg-accent-foreground/90 transition-colors"
                >
                  Explore Trails
                </a>
                <a
                  href="/heritage-interactive-map"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-accent-foreground text-accent-foreground font-medium rounded-md hover:bg-accent-foreground hover:text-accent transition-colors"
                >
                  View Heritage Map
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-primary text-primary-foreground py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-heading font-semibold text-lg mb-4">Creative Twinning</h3>
                <p className="text-primary-foreground/80 text-sm leading-relaxed">
                  Promoting cross-border heritage tourism and cultural exchange between Serbia and Montenegro through innovative digital experiences.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium mb-4">Quick Links</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="/homepage" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">Home</a></li>
                  <li><a href="/about-project" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">About Project</a></li>
                  <li><a href="/thematic-trails-overview" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">Thematic Trails</a></li>
                  <li><a href="/tourism-offerings" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">Tourism Offerings</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium mb-4">Contact</h4>
                <p className="text-primary-foreground/80 text-sm leading-relaxed">
                  For inquiries and partnership opportunities, reach out to us at info@creative-twinning.com
                </p>
              </div>
            </div>
            
            <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center">
              <p className="text-primary-foreground/60 text-sm">
                © {new Date()?.getFullYear()} Creative Twinning - Kreativna Saradnja. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContactAndPartnersPage;